<template>
  <div class="admin-context-view">
    <div v-if="isLoading" class="tw-state">
      <b-loading :is-full-page="false" :active="true" />
      <p>Loading the current inferred context...</p>
    </div>

    <div v-else-if="loadError" class="tw-state tw-state--error">
      <p>{{ loadError }}</p>
      <b-button type="is-light" @click="load">Retry</b-button>
    </div>

    <template v-else>
      <div v-if="amendmentError" class="tw-state tw-state--error">
        <p>{{ amendmentError }}</p>
      </div>

      <section class="admin-context-view__summary tw-card">
        <p class="section-eyebrow">Executive summary</p>
        <ul class="summary-lines">
          <li>{{ executiveSummary.profile }}</li>
          <li>{{ executiveSummary.baseline }}</li>
          <li>{{ executiveSummary.workflowRules }}</li>
        </ul>

        <div v-if="executiveSummary.topOpenUnknowns && executiveSummary.topOpenUnknowns.length" class="summary-unknowns">
          <p class="draft-field__label">Top open unknowns</p>
          <ul class="draft-list">
            <li v-for="(item, index) in executiveSummary.topOpenUnknowns" :key="`unknown-${index}`">
              <b-tag :type="priorityTagType(item.priority)">{{ item.priority }}</b-tag>
              {{ item.questionOrRule }}
            </li>
          </ul>
        </div>
      </section>

      <section class="admin-context-view__tier tw-card">
        <p class="section-eyebrow">Customer profile</p>
        <div
          v-for="item in profileItems"
          :key="`profile-${item.dimensionKey}`"
          class="context-item"
        >
          <div class="context-item__header">
            <h4>{{ dimensionLabel(item.dimensionKey) }}</h4>
            <div class="context-item__badges">
              <b-tag :type="classificationTagType(item.evidenceClassification)">{{ item.evidenceClassification }}</b-tag>
              <b-tag v-if="item.confidence">{{ item.confidence }}</b-tag>
            </div>
          </div>

          <p class="context-item__conclusion">{{ item.conclusion }}</p>

          <dl class="context-item__meta">
            <div>
              <dt>Source type</dt>
              <dd>{{ item.sourceType || '—' }}</dd>
            </div>
            <div>
              <dt>Confirmation status</dt>
              <dd>{{ item.confirmationStatus || '—' }}</dd>
            </div>
          </dl>

          <ul v-if="item.supportingEvidence && item.supportingEvidence.length" class="draft-list">
            <li v-for="(evidence, index) in item.supportingEvidence" :key="`profile-${item.dimensionKey}-evidence-${index}`">
              {{ evidence }}
            </li>
          </ul>

          <AmendmentBox
            :amendments="item.amendments"
            :is-submitting="isSubmittingFieldPath(`profile.${item.dimensionKey}`)"
            @submit="note => submitAmendment(`profile.${item.dimensionKey}`, note)"
          />
        </div>
      </section>

      <section class="admin-context-view__tier tw-card">
        <p class="section-eyebrow">Operating baseline</p>
        <div
          v-for="item in baselineItems"
          :key="`baseline-${item.dimensionKey}`"
          class="context-item"
        >
          <div class="context-item__header">
            <h4>{{ dimensionLabel(item.dimensionKey) }}</h4>
            <div class="context-item__badges">
              <b-tag :type="classificationTagType(item.evidenceClassification)">{{ item.evidenceClassification }}</b-tag>
              <b-tag v-if="item.confidence">{{ item.confidence }}</b-tag>
            </div>
          </div>

          <p class="context-item__conclusion">{{ item.conclusion }}</p>

          <dl class="context-item__meta">
            <div>
              <dt>Observation period</dt>
              <dd>{{ formatObservationPeriod(item.observationPeriodStart, item.observationPeriodEnd) }}</dd>
            </div>
            <div>
              <dt>Scope limitation</dt>
              <dd>{{ item.scopeLimitation || '—' }}</dd>
            </div>
          </dl>

          <AmendmentBox
            :amendments="item.amendments"
            :is-submitting="isSubmittingFieldPath(`baseline.${item.dimensionKey}`)"
            @submit="note => submitAmendment(`baseline.${item.dimensionKey}`, note)"
          />
        </div>
      </section>

      <section v-if="criticalEvidence.length" class="admin-context-view__tier tw-card">
        <p class="section-eyebrow">Critical evidence, limitations &amp; contradictions</p>
        <div
          v-for="(item, index) in criticalEvidence"
          :key="`critical-${index}`"
          class="confirmation-item"
        >
          <div class="context-item__header">
            <h4>{{ dimensionLabel(item.dimensionKey) }}</h4>
            <div class="context-item__badges">
              <b-tag type="is-warning">{{ item.reason }}</b-tag>
              <b-tag>{{ item.tier }}</b-tag>
            </div>
          </div>
          <p class="context-item__conclusion">{{ item.detail }}</p>
        </div>
      </section>

      <section class="admin-context-view__tier tw-card">
        <p class="section-eyebrow">Confirmation queue</p>
        <p v-if="!confirmationQueue.length" class="draft-viewer__empty">
          No open confirmation questions or candidate workflow-rule patterns right now.
        </p>
        <div
          v-for="(entry, index) in confirmationQueue"
          :key="`confirmation-${index}`"
          class="confirmation-item"
        >
          <div class="context-item__header">
            <h4>{{ entry.questionOrRule }}</h4>
            <div class="context-item__badges">
              <b-tag :type="priorityTagType(entry.priority)">{{ entry.priority }}</b-tag>
              <b-tag>{{ entry.component }}</b-tag>
            </div>
          </div>
          <p v-if="entry.rationale" class="context-item__conclusion">{{ entry.rationale }}</p>
          <p v-if="entry.recommendedConfirmingParty" class="confirmation-item__party">
            Recommended confirming party: {{ entry.recommendedConfirmingParty }}
          </p>
        </div>
      </section>

      <ContextHistoryView />
    </template>
  </div>
</template>

<script>
import { fetchAdminContextView, submitContextAmendment } from '@/services/companyContextService'
import AmendmentBox from './AmendmentBox.vue'
import ContextHistoryView from './ContextHistoryView.vue'

export default {
  name: 'AdminContextView',
  components: {
    AmendmentBox,
    ContextHistoryView,
  },
  data() {
    return {
      isLoading: false,
      loadError: null,
      // Review fix: a separate error state from loadError — a failed amendment submission
      // must not wipe the already-loaded view (Executive Summary, Profile, Baseline,
      // Confirmation Queue) behind a full-page error screen.
      amendmentError: null,
      executiveSummary: { profile: '', baseline: '', workflowRules: '', topOpenUnknowns: [] },
      profileItems: [],
      baselineItems: [],
      criticalEvidence: [],
      confirmationQueue: [],
      submittingFieldPaths: {},
    }
  },
  created() {
    this.load()
  },
  methods: {
    getToken() {
      return this.$cookies.get('teamwrkr-token')
    },
    async load() {
      this.isLoading = true
      this.loadError = null
      try {
        const view = await fetchAdminContextView(this.axios, this.getToken())
        this.executiveSummary = view?.executiveSummary || this.executiveSummary
        this.profileItems = view?.profileItems || []
        this.baselineItems = view?.baselineItems || []
        this.criticalEvidence = view?.criticalEvidence || []
        this.confirmationQueue = view?.confirmationQueue || []
      } catch (error) {
        this.loadError = error?.response?.data?.message || 'Unable to load the current context.'
      } finally {
        this.isLoading = false
      }
    },
    isSubmittingFieldPath(fieldPath) {
      return !!this.submittingFieldPaths[fieldPath]
    },
    async submitAmendment(fieldPath, note) {
      if (!note || !note.trim()) return
      this.amendmentError = null
      this.submittingFieldPaths = { ...this.submittingFieldPaths, [fieldPath]: true }
      try {
        const amendment = await submitContextAmendment(this.axios, this.getToken(), { fieldPath, note })
        this.appendAmendment(fieldPath, amendment)
      } catch (error) {
        this.amendmentError = error?.response?.data?.message || 'Unable to save that amendment.'
      } finally {
        this.submittingFieldPaths = { ...this.submittingFieldPaths, [fieldPath]: false }
      }
    },
    appendAmendment(fieldPath, amendment) {
      if (!amendment) return
      const [tier, dimensionKey] = fieldPath.split('.')
      const items = tier === 'baseline' ? this.baselineItems : this.profileItems
      const item = items.find(candidate => candidate.dimensionKey === dimensionKey)
      if (item) {
        item.amendments = [...(item.amendments || []), amendment]
      }
    },
    dimensionLabel(value) {
      return `${value || ''}`
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, char => char.toUpperCase())
        .trim()
    },
    classificationTagType(classification) {
      switch (classification) {
        case 'observed': return 'is-success'
        case 'inferred': return 'is-info'
        case 'needs_confirmation': return 'is-warning'
        case 'unknown': return 'is-light'
        default: return 'is-light'
      }
    },
    priorityTagType(priority) {
      switch (priority) {
        case 'high': return 'is-danger'
        case 'medium': return 'is-warning'
        case 'low': return 'is-light'
        default: return 'is-light'
      }
    },
    formatObservationPeriod(start, end) {
      if (!start && !end) return '—'
      const format = value => {
        if (!value) return '?'
        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? '?' : date.toISOString().slice(0, 10)
      }
      return `${format(start)} to ${format(end)}`
    },
  },
}
</script>

<style lang="scss" scoped>
// Review fix: every level below is a `display: grid` stack nested inside another one, and a
// grid item's default min-width is `auto` (its content's natural width), not 0 — so a long
// unbroken line doesn't just fail to wrap at the text level, it pushes the CARD containing it
// wider than its own grid track, which pushes THAT container wider than its parent, all the
// way up past the page's max-width. min-width: 0 has to be set at every one of these nesting
// levels, not just on the text-bearing leaf elements, or the overflow just resurfaces one
// level up. This component is itself a grid item of BusinessContext.vue's `.context-layout`.
.admin-context-view {
  display: grid;
  gap: $space-5;
  min-width: 0;
}

.admin-context-view__summary {
  min-width: 0;
}

.summary-lines {
  margin: 0;
  padding-left: 1.1rem;
  color: $color-text;
  list-style: disc;
}

.summary-unknowns {
  margin-top: $space-4;
}

.admin-context-view__tier {
  display: grid;
  gap: $space-4;
  min-width: 0;
}

.context-item {
  border: 1px solid $color-border;
  border-radius: $radius-lg;
  background: $color-surface;
  padding: $space-5;
  display: grid;
  gap: $space-3;
  min-width: 0;
}

.context-item__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: $space-3;

  h4 {
    margin: 0;
    // Flex/grid items don't shrink below their content's natural width by default
    // (min-width: auto) — without this, a long title just pushes past the card and the
    // page overflows sideways instead of wrapping onto a new line.
    min-width: 0;
    flex: 1 1 240px;
    overflow-wrap: anywhere;
    font-size: $font-size-md;
  }
}

.context-item__badges {
  display: flex;
  flex-wrap: wrap;
  gap: $space-2;
  flex-shrink: 0;
}

.context-item__conclusion {
  margin: 0;
  color: $color-text;
  line-height: 1.7;
  overflow-wrap: anywhere;
}

.context-item__meta {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: $space-3;
  margin: 0;

  // Same min-width: auto issue as above, on the grid item wrapping each dt/dd pair.
  > div {
    min-width: 0;
  }

  dt {
    color: $color-text-muted;
    font-size: $font-size-sm;
    font-weight: 700;
  }

  dd {
    margin: 0;
    color: $color-text;
    overflow-wrap: anywhere;
  }
}

.confirmation-item {
  border: 1px solid $color-border-muted;
  border-radius: $radius-lg;
  padding: $space-4 $space-5;
  min-width: 0;
}

.confirmation-item__party {
  margin: $space-2 0 0;
  color: $color-text-muted;
  font-size: $font-size-sm;
}
</style>
